/** @typedef {import('./helpscout').default} HelpScoutService */

/**
 * Shared state manager for HelpScout documentation data
 * Prevents duplicate API calls by maintaining a single source of truth
 * Persists data in sessionStorage for instant page refresh
 */
class HelpScoutState {
  constructor() {
    this.collections = null;
    this.collectionsLoading = false;
    this.collectionsPromise = null;
    this.categoryCache = new Map(); // collectionId -> categories data
    this.listeners = new Set();
    this.storagePrefix = 'helpscout:';
    this.storageAvailable = this.isSessionStorageAvailable();
    // Load initial state from sessionStorage
    this.loadFromSessionStorage();
  }

  /**
   * Check if sessionStorage is available
   */
  isSessionStorageAvailable() {
    try {
      const test = '__storage_test__';
      sessionStorage.setItem(test, test);
      sessionStorage.removeItem(test);
      return true;
    } catch (e) {
      console.warn('[HelpScout State] SessionStorage not available');
      return false;
    }
  }

  /**
   * Load state from sessionStorage on initialization
   */
  loadFromSessionStorage() {
    if (!this.storageAvailable) return;

    try {
      // Load collections
      const collectionsKey = `${this.storagePrefix}collections`;
      const collectionsData = sessionStorage.getItem(collectionsKey);
      if (collectionsData) {
        this.collections = JSON.parse(collectionsData);
      }

      // Load categories
      const categoriesPrefix = `${this.storagePrefix}categories:`;
      for (let i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i);
        if (key && key.startsWith(categoriesPrefix)) {
          const collectionId = key.substring(categoriesPrefix.length);
          const categoriesData = sessionStorage.getItem(key);
          if (categoriesData) {
            this.categoryCache.set(collectionId, JSON.parse(categoriesData));
          }
        }
      }
    } catch (e) {
      console.warn('[HelpScout State] Error loading from sessionStorage:', e);
    }
  }

  /**
   * Save collections to sessionStorage
   */
  saveCollectionsToSessionStorage() {
    if (!this.storageAvailable || !this.collections) return;

    try {
      const key = `${this.storagePrefix}collections`;
      sessionStorage.setItem(key, JSON.stringify(this.collections));
    } catch (e) {
      if (e.name === 'QuotaExceededError') {
        console.warn('[HelpScout State] SessionStorage quota exceeded');
      } else {
        console.warn('[HelpScout State] Error saving collections:', e);
      }
    }
  }

  /**
   * Save categories to sessionStorage
   */
  saveCategoriesToSessionStorage(collectionId, categories) {
    if (!this.storageAvailable) return;

    try {
      const key = `${this.storagePrefix}categories:${collectionId}`;
      sessionStorage.setItem(key, JSON.stringify(categories));
    } catch (e) {
      if (e.name === 'QuotaExceededError') {
        console.warn('[HelpScout State] SessionStorage quota exceeded');
      } else {
        console.warn('[HelpScout State] Error saving categories:', e);
      }
    }
  }

  /**
   * Subscribe to state changes
   * @param {Function} callback - Function to call when state changes
   * @returns {Function} Unsubscribe function
   */
  subscribe(callback) {
    this.listeners.add(callback);
    return () => this.listeners.delete(callback);
  }

  /**
   * Notify all listeners of state change
   */
  notify() {
    this.listeners.forEach(callback => callback(this.getState()));
  }

  /**
   * Get current state snapshot
   */
  getState() {
    return {
      collections: this.collections,
      loading: this.collectionsLoading,
    };
  }

  /**
   * Get collections data (singleton pattern - fetch once and cache)
   * @param {HelpScoutService} helpScoutService - The HelpScout service instance
   * @returns {Promise} Collections data
   */
  async getCollections(helpScoutService) {
    // If we already have the data, return it immediately
    // (may have been loaded from sessionStorage on init)
    if (this.collections) {
      return this.collections;
    }

    // If a fetch is in progress, wait for it
    if (this.collectionsPromise) {
      return this.collectionsPromise;
    }

    // Start a new fetch
    this.collectionsLoading = true;
    this.notify();

    this.collectionsPromise = (async () => {
      try {
        const collectionsData = await helpScoutService.getCollections();
        if (collectionsData) {
          this.collections = collectionsData;
        } else {
          this.collections = [];
        }

        // Save to sessionStorage for instant page refresh
        this.saveCollectionsToSessionStorage();

        return this.collections;
      } catch (error) {
        console.error('Error loading collections in shared state:', error);
        this.collections = [];
        throw error;
      } finally {
        this.collectionsLoading = false;
        this.collectionsPromise = null;
        this.notify();
      }
    })();

    return this.collectionsPromise;
  }

  /**
   * Find a collection by ID or slug
   * @param {string} identifier - Collection ID or slug
   * @returns {Object|null} Collection object or null
   */
  findCollection(identifier) {
    if (!this.collections) return null;

    return this.collections.find(
      c => c.id === identifier || c.slug === identifier
    ) || null;
  }

  /**
   * Get categories for a collection (with caching)
   * @param {string} collectionId - Collection ID
   * @param {HelpScoutService} helpScoutService - The HelpScout service instance
   * @returns {Promise} Categories data
   */
  async getCategories(collectionId, helpScoutService) {
    // Exclude Help Scout's auto-created "Uncategorized" category
    const stripUncategorized = (list) =>
      (list || []).filter(
        (category) => (category.name || '').trim().toLowerCase() !== 'uncategorized'
      );

    // Check cache first (may have been loaded from sessionStorage)
    if (this.categoryCache.has(collectionId)) {
      return stripUncategorized(this.categoryCache.get(collectionId));
    }

    // Fetch and cache
    try {
      const categoriesData = await helpScoutService.getCategories(collectionId);

      let categories = [];
      if (categoriesData) {
        categories = stripUncategorized(categoriesData);
      }

      // Cache in memory
      this.categoryCache.set(collectionId, categories);

      // Save to sessionStorage for instant page refresh
      this.saveCategoriesToSessionStorage(collectionId, categories);

      return categories;
    } catch (error) {
      console.error('Error loading categories in shared state:', error);
      return [];
    }
  }

  /**
   * Clear all cached data (both memory and sessionStorage)
   */
  clear() {
    this.collections = null;
    this.collectionsLoading = false;
    this.collectionsPromise = null;
    this.categoryCache.clear();
    this.clearSessionStorage();
    this.notify();
  }

  /**
   * Clear categories cache for a specific collection
   */
  clearCategories(collectionId) {
    if (collectionId) {
      this.categoryCache.delete(collectionId);

      // Also clear from sessionStorage
      if (this.storageAvailable) {
        try {
          const key = `${this.storagePrefix}categories:${collectionId}`;
          sessionStorage.removeItem(key);
        } catch (e) {
          console.warn('[HelpScout State] Error clearing category from sessionStorage:', e);
        }
      }
    } else {
      this.categoryCache.clear();

      // Clear all categories from sessionStorage
      if (this.storageAvailable) {
        try {
          const categoriesPrefix = `${this.storagePrefix}categories:`;
          const keysToRemove = [];
          for (let i = 0; i < sessionStorage.length; i++) {
            const key = sessionStorage.key(i);
            if (key && key.startsWith(categoriesPrefix)) {
              keysToRemove.push(key);
            }
          }
          keysToRemove.forEach(key => sessionStorage.removeItem(key));
        } catch (e) {
          console.warn('[HelpScout State] Error clearing categories from sessionStorage:', e);
        }
      }
    }
  }

  /**
   * Clear all sessionStorage entries with our prefix
   */
  clearSessionStorage() {
    if (!this.storageAvailable) return;

    try {
      const keysToRemove = [];
      for (let i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i);
        if (key && key.startsWith(this.storagePrefix)) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach(key => sessionStorage.removeItem(key));
    } catch (e) {
      console.warn('[HelpScout State] Error clearing sessionStorage:', e);
    }
  }
}

// Export singleton instance
export default new HelpScoutState();

